import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import StickyCTA from "./StickyCTA";
import FloatingQuote from "./FloatingQuote";
import CookieConsent from "./CookieConsent";
import ScrollToTop from "./ScrollToTop";
import Analytics from "../lib/analytics";
import useLenis from "../lib/useLenis";
import { CompareDock } from "./marketplace/CompareDock";

/* URL normaliser — kills the trailing-slash / uppercase / tracking-param
   variants that Google Search Console is reporting as duplicate URLs.
   Redirects to the canonical clean form using history.replaceState so the
   URL bar updates without a full navigation. */
function useCanonicalUrl() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    let { pathname, search } = location;
    let changed = false;

    // 1. Lowercase pathname (Google treats /VFD-Repair and /vfd-repair as different)
    const lower = pathname.toLowerCase();
    if (lower !== pathname) { pathname = lower; changed = true; }

    // 2. Strip trailing slash (except root "/")
    if (pathname.length > 1 && pathname.endsWith("/")) {
      pathname = pathname.replace(/\/+$/, "");
      changed = true;
    }

    // 3. Strip tracking / hash query params (utm_*, fbclid, gclid, sessionid)
    if (search) {
      const params = new URLSearchParams(search);
      const bad = [];
      for (const k of params.keys()) {
        if (/^utm_/i.test(k) || /^(fbclid|gclid|sessionid|mc_cid|mc_eid|ref)$/i.test(k)) bad.push(k);
      }
      bad.forEach((k) => params.delete(k));
      const clean = params.toString();
      if (clean !== search.slice(1)) { search = clean ? `?${clean}` : ""; changed = true; }
    }

    if (changed) navigate(pathname + search, { replace: true });
  }, [location, navigate]);
}

function LayoutInner({ children }) {
  useLenis();
  useCanonicalUrl();
  const location = useLocation();
  const serviceMode = /^(\/services|\/spm-solutions|\/power-electronics|\/brands|\/industries|\/service-areas|\/request)/.test(location.pathname);
  const workspaceMode = /^(\/admin|\/seller)/.test(location.pathname);
  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      <ScrollToTop />
      <Analytics />
      <Header />
      <main className="flex-1 pt-16 md:pt-20 pb-16 md:pb-0">{workspaceMode && <CookieConsent inline />}{children}</main>
      <Footer />
      {serviceMode && <StickyCTA />}
      {serviceMode && <FloatingQuote />}
      <CompareDock />
      {!workspaceMode && <CookieConsent />}
    </div>
  );
}

export default function Layout({ children }) {
  return <LayoutInner>{children}</LayoutInner>;
}
